import { DateTime } from 'luxon';
import type { DurationLikeObject } from 'luxon';
import { DateRangeKey } from '@newo/ui-kit';
import useSettingsStore from '@/store/settings.ts';
import { DATE_FORMAT } from '@/const';

export const tzDatetime = (datetime = DateTime.now().toISO()) => {
    const { timeZone, isUTC } = useSettingsStore.getState().settings;
    if (isUTC) {
        return DateTime.fromISO(datetime).toUTC();
    }
    return DateTime.fromISO(datetime).setZone(timeZone);
};

export const formatDate = (date: string, format = DATE_FORMAT.FULL_12H) => {
    const datetime = date.endsWith('Z') ? date : `${date}Z`;
    return tzDatetime(datetime).toFormat(format);
};

export type DateRangeResult = {
    from_datetime?: string;
    to_datetime?: string;
    from?: DateTime;
    to?: DateTime;
};

export const setAsTimezoneDateTime = (datetime: string | Date, tz?: string) => {
    const { isUTC, timeZone } = useSettingsStore.getState().settings;
    const luxonDateTime = DateTime.fromJSDate(new Date(datetime));
    const storeTz = isUTC ? 'utc' : timeZone;
    return luxonDateTime.setZone(tz ?? storeTz, { keepLocalTime: true });
};

// TODO: don't pass formatted date to the function
export const processDateParams = <
    T extends { from_datetime?: string; to_datetime?: string }
>(
    params: T
): T => {
    const { isUTC, timeZone } = useSettingsStore.getState().settings;
    const processedParams = {
        ...params
    };
    if (params.from_datetime) {
        const formatted = isUTC
            ? setAsTimezoneDateTime(params.from_datetime, 'utc').toISO({
                  includeOffset: false
              })
            : setAsTimezoneDateTime(params.from_datetime, timeZone)
                  .toUTC()
                  .toString();
        processedParams.from_datetime = formatted ?? undefined;
    }

    if (params.to_datetime) {
        const formatted = isUTC
            ? setAsTimezoneDateTime(params.to_datetime, 'utc').toISO({
                  includeOffset: false
              })
            : setAsTimezoneDateTime(params.to_datetime, timeZone)
                  .toUTC()
                  .toString();
        processedParams.to_datetime = formatted ?? undefined;
    }
    return processedParams;
};

export const getDateRange = (
    dateRange?: DateRangeKey | null
): DateRangeResult => {
    const now = tzDatetime();

    const ranges: Record<
        DateRangeKey,
        { unit: keyof DurationLikeObject; value: number }
    > = {
        [DateRangeKey.LAST_15_MINUTES]: { unit: 'minutes', value: 15 },
        [DateRangeKey.LAST_1_HOUR]: { unit: 'hours', value: 1 },
        [DateRangeKey.LAST_24_HOURS]: { unit: 'hours', value: 24 },
        [DateRangeKey.LAST_48_HOURS]: { unit: 'hours', value: 48 },
        [DateRangeKey.LAST_72_HOURS]: { unit: 'hours', value: 72 },
        [DateRangeKey.LAST_7_DAYS]: { unit: 'days', value: 7 },
        [DateRangeKey.LAST_30_DAYS]: { unit: 'days', value: 30 }
    };

    const range = dateRange ? ranges[dateRange] : undefined;

    if (!range) return {};

    const from = now.minus({ [range.unit]: range.value });

    const toISOString = (dt: DateTime): string | undefined =>
        dt.toISO() ?? undefined;

    return {
        from_datetime: toISOString(
            range.unit === 'days' ? from.startOf('day') : from
        ),
        to_datetime: toISOString(now)
    };
};
export const getDaysDiff = (date1: string, date2: string) => {
    const date1Luxon = DateTime.fromISO(date1);
    const date2Luxon = DateTime.fromISO(date2);
    return date1Luxon.diff(date2Luxon, 'days').days;
};
